// src/components/expediente/TimelineSoap.js
import React from 'react';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ScienceIcon from '@mui/icons-material/Science';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';

import { useTimelineSoap } from '../../hooks/useTimelineSoap'; // 🚀 IMPORTAMOS EL CEREBRO
import { useClinica } from '../../context/ClinicaContext';

export default function TimelineSoap({ consultas, onVerInforme }) {
    // 1. EXTRAEMOS LA LÓGICA
    const {
        filtroActivo, setFiltroActivo,
        consultasFiltradas, metricas,
        safeFormatDate
    } = useTimelineSoap(consultas);
    const { definiciones, especialidades } = useClinica();

    // 2. ESTADOS DE EXCEPCIÓN (Vistas vacías)
    if (!consultas || consultas.length === 0) {
        return (
            <div className="flex flex-col justify-center items-center h-64 w-full bg-slate-50 border-2 border-dashed border-slate-200 rounded-3xl">
                <MedicalServicesIcon className="text-slate-300 w-12 h-12 mb-3" />
                <p className="text-slate-500 font-bold text-sm">El expediente clínico del paciente está en blanco.</p>
                <p className="text-slate-400 text-xs mt-1">No hay actos médicos registrados previamente.</p>
            </div>
        );
    }

    // 3. RENDERIZADO PRINCIPAL
    return (
        <div className="w-full">
            {/* 📊 PANEL GRÁFICO SUPERIOR (DASHBOARD DE GRAVEDAD) */}
            <div className="mb-8 p-4 bg-slate-900 rounded-3xl shadow-xl flex flex-col md:flex-row gap-3 items-center justify-between">
                <div className="px-4 text-white text-center md:text-left">
                    <h4 className="text-xs font-black uppercase tracking-widest text-slate-400">Análisis Evolutivo</h4>
                    <p className="text-lg font-black leading-tight">Mapeo de Morbilidad</p>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-2">
                    <button onClick={() => setFiltroActivo('TODOS')} className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${filtroActivo === 'TODOS' ? 'bg-white text-slate-900 shadow-md' : 'bg-white/10 text-white hover:bg-white/20'}`}>
                        Total ({metricas.TODOS})
                    </button>
                    <button onClick={() => setFiltroActivo('ALTA')} className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center gap-1.5 ${filtroActivo === 'ALTA' ? 'bg-rose-500 text-white shadow-[0_0_15px_rgba(244,63,94,0.4)]' : 'bg-rose-500/10 text-rose-300 hover:bg-rose-500/20'}`}>
                        <WarningRoundedIcon fontSize="small" /> Graves ({metricas.ALTA})
                    </button>
                    <button onClick={() => setFiltroActivo('MEDIA')} className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center gap-1.5 ${filtroActivo === 'MEDIA' ? 'bg-amber-500 text-white shadow-[0_0_15px_rgba(245,158,11,0.4)]' : 'bg-amber-500/10 text-amber-300 hover:bg-amber-500/20'}`}>
                        <ReportProblemRoundedIcon fontSize="small" /> Moderados ({metricas.MEDIA})
                    </button>
                    <button onClick={() => setFiltroActivo('BAJA')} className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center gap-1.5 ${filtroActivo === 'BAJA' ? 'bg-emerald-500 text-white shadow-[0_0_15px_rgba(16,185,129,0.4)]' : 'bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20'}`}>
                        <CheckCircleOutlineRoundedIcon fontSize="small" /> Leves ({metricas.BAJA})
                    </button>
                </div>
            </div>

            {/* 🏥 TIMELINE CRONOLÓGICO Y VISUAL */}
            <div className="relative pl-6 md:pl-10 pb-4">
                <div className="absolute left-3 md:left-5 top-4 bottom-0 w-1 bg-slate-200 rounded-full" />

                {consultasFiltradas.length === 0 && (
                    <div className="py-8 text-center text-slate-500 font-bold text-sm">
                        No hay registros que coincidan con este nivel de gravedad.
                    </div>
                )}

                {consultasFiltradas.map((c) => (
                    <div key={c.id || c._id} className="relative mb-8 last:mb-0 group animate-fade-in">
                        <div className={`absolute -left-[27px] md:-left-[35px] top-4 w-6 h-6 rounded-full border-4 border-white ${c._gravedad.dotBg} ${c._gravedad.shadow} z-10 transition-transform group-hover:scale-125 duration-300`} />
                        <div className={`ml-4 md:ml-8 bg-white rounded-2xl border-l-4 border-y border-r border-r-slate-100 border-y-slate-100 ${c._gravedad.cardBorder} shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden`}>

                            <div className={`px-5 py-3 ${c._gravedad.cardBg} border-b border-slate-100/50 flex flex-wrap justify-between items-center gap-3`}>
                                <div className="flex items-center gap-3">
                                    <span className="text-sm font-black text-slate-800 tracking-tight">{safeFormatDate(c.fecha, "dd 'de' MMMM, yyyy")}</span>
                                    <span className="text-xs font-bold text-slate-400">{safeFormatDate(c.fecha, "HH:mm")} hrs</span>
                                    {/* Especialidad: solo aporta información en clínicas con varias */}
                                    {especialidades.length > 1 && definiciones[c.especialidad] && (
                                        <span className="px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-widest text-white" style={{ backgroundColor: definiciones[c.especialidad].color }}>
                                            {definiciones[c.especialidad].nombre}
                                        </span>
                                    )}
                                </div>
                                <span className={`px-3 py-1 rounded-lg text-[10px] font-black tracking-widest uppercase flex items-center gap-1.5 border border-white/50 ${c._gravedad.badgeBg} ${c._gravedad.badgeText}`}>
                                    {c._gravedad.icon} {c._gravedad.nivel}
                                </span>
                            </div>

                            <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div>
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1.5"><ScienceIcon fontSize="inherit" /> Motivo / Sintomatología</p>
                                        <p className="text-sm font-semibold text-slate-700 leading-relaxed bg-slate-50 p-3 rounded-xl border border-slate-100">{c.motivo || c.sintomas || 'No registrado'}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1.5"><MedicalServicesIcon fontSize="inherit" /> Juicio Clínico</p>
                                        <p className="text-sm font-black text-slate-900 leading-relaxed bg-blue-50/50 p-3 rounded-xl border border-blue-100">{c.diagnostico || 'Pendiente de evaluación'}</p>
                                    </div>
                                </div>
                                <div className="flex flex-col h-full space-y-4">
                                    <div className="flex-1">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Plan de Tratamiento y Recetas</p>
                                        <p className="text-sm font-bold text-slate-700 leading-relaxed bg-slate-50 p-3 rounded-xl border border-slate-100 h-full">{c.tratamiento || 'Ninguna pauta registrada.'}</p>
                                    </div>
                                    <div className="pt-2">
                                        <button onClick={() => onVerInforme(c)} className="w-full px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-black uppercase tracking-widest rounded-xl transition-all shadow-md focus:outline-none focus:ring-4 focus:ring-slate-900/20 flex justify-center items-center gap-2">
                                            Abrir Informe Completo &rarr;
                                        </button>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}